import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Icon } from '@iconify/react';
import { getCourseCompletePercentage } from '../../../../services/userService';

const PRIMARY = '#7083FF';

const ProgressBar = memo(({ progress }) => {
  const safeProgress = Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0;
  return (
    <div
      className="h-2 bg-gray-200 rounded-full overflow-hidden"
      role="progressbar"
      aria-valuenow={safeProgress}
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{ width: `${safeProgress}%`, backgroundColor: PRIMARY }}
      />
    </div>
  );
});

function formatDuration(duration) {
  if (!duration) return '—';
  
  if (typeof duration === 'string' && duration.includes(':')) {
    return duration;
  }
  
  const seconds = parseFloat(duration);
  if (!isNaN(seconds)) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  
  return duration;
}

function formatTotalDuration(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hrs}hr ${mins.toString().padStart(2, '0')}min`;
}

function CourseModule({ courseModules, courseId, onContentClick, selectedContent, onProgressUpdate, courseName }) {
  const [expandedModule, setExpandedModule] = useState(() => {
    try {
      const saved = localStorage.getItem(`module-expanded-${courseId}`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [courseProgress, setCourseProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const activeItemRef = useRef(null);
  const lastAutoExpandedSelectedKeyRef = useRef(null);

  const updateProgress = useCallback(async () => {
    if (!courseId) return;
    try {
      const response = await getCourseCompletePercentage(courseId);
      if (response?.data?.overallCompletionPercentage) {
        const rawProgress = parseFloat(response.data.overallCompletionPercentage);
        const progress = Number.isFinite(rawProgress) ? Math.min(100, Math.max(0, rawProgress)) : 0;
        setCourseProgress((prev) => (prev !== progress ? progress : prev));
        if (onProgressUpdate) onProgressUpdate(progress);
      }
    } catch (err) {
      console.error('Error updating course progress:', err);
    }
  }, [courseId, onProgressUpdate]);

  const totalChapters = courseModules.reduce((sum, module) => sum + (module.content?.length || 0), 0);
  const totalDuration = courseModules.reduce((sum, module) => {
    const moduleDuration = module.content?.reduce((mSum, item) => {
      const duration = parseFloat(item.duration) || 0;
      return mSum + duration;
    }, 0) || 0;
    return sum + moduleDuration;
  }, 0);

  useEffect(() => {
    if (!selectedContent || !courseModules?.length) return;

    const selectedKey = `${selectedContent.type || 'content'}:${selectedContent.id}`;

    // Only auto-expand when the selected content actually changes.
    // This prevents user clicks (manual module expand) from being overridden
    // during background module list updates (e.g. attempted/completed assessment flows).
    if (lastAutoExpandedSelectedKeyRef.current === selectedKey) return;

    lastAutoExpandedSelectedKeyRef.current = selectedKey;

    const moduleWithContent = courseModules.find((m) =>
      m.content?.some((item) => item.id === selectedContent.id && item.type === selectedContent.type)
    );
    if (moduleWithContent) setExpandedModule(moduleWithContent.id);
  }, [selectedContent?.id, selectedContent?.type, courseModules]);

  useEffect(() => {
    if (selectedContent?.id && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [selectedContent?.id]);

  useEffect(() => {
    if (courseId) {
      setIsLoading(true);
      updateProgress().finally(() => setIsLoading(false));
    }
  }, [courseId, updateProgress]);

  useEffect(() => {
    if (courseModules?.length) {
      // If all content items are completed locally, set progress to 100% immediately
      // This avoids race conditions where the API hasn't persisted the latest completion yet
      const allContent = courseModules.flatMap((m) => m.content || []);
      const allCompleted = allContent.length > 0 && allContent.every((item) => item.completed === 1);
      if (allCompleted) {
        setCourseProgress(100);
        if (onProgressUpdate) onProgressUpdate(100);
      } else {
        updateProgress();
      }
    }
  }, [courseModules, updateProgress]);

  useEffect(() => {
    if (expandedModule != null && courseId) {
      localStorage.setItem(`module-expanded-${courseId}`, JSON.stringify(expandedModule));
    }
  }, [expandedModule, courseId]);

  const toggleModule = (moduleId) => {
    setExpandedModule((prev) => (prev === moduleId ? null : moduleId));
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  if (isLoading) {
    return (
      <div className="p-6" aria-busy="true">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#7083FF]" role="status" aria-label="Loading" />
        </div>
      </div>
    );
  }

  const chapterCount = (module) => (Array.isArray(module.content) ? module.content.length : 0);

  return (
    <div className="p-6" role="navigation" aria-label="Course modules">
      {courseName && (
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">{courseName}</h2>
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            <span className="flex items-center gap-1.5">
              <Icon icon="mdi:file-document-outline" width="16" height="16" />
              {totalChapters} Chapters
            </span>
            <span className="flex items-center gap-1.5">
              <Icon icon="mdi:clock-outline" width="16" height="16" />
              {formatTotalDuration(totalDuration)}
            </span>
          </div>
          <div className="mb-1">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-gray-500">In Progress</span>
              <span className="font-semibold text-gray-800">{courseProgress.toFixed(0)}%</span>
            </div>
            <ProgressBar progress={courseProgress} />
          </div>
        </div>
      )}

      <div className="space-y-1 max-h-[min(50vh,400px)] overflow-y-auto scrollbar-thin pr-1 -mr-1">
        {Array.isArray(courseModules) &&
          courseModules.map((module) => {
            const isExpanded = expandedModule === module.id;
            const chapters = chapterCount(module);
            return (
              <div key={module.id} className="border-b border-gray-100 last:border-b-0 pb-2">
                <button
                  type="button"
                  onClick={() => toggleModule(module.id)}
                  onKeyDown={(e) => handleKeyPress(e, () => toggleModule(module.id))}
                  className="w-full flex items-center justify-between gap-2 py-3 px-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  aria-expanded={isExpanded}
                  aria-controls={`module-content-${module.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-700 text-sm truncate mb-0.5">{module.module_name}</p>
                    <p className="text-xs text-gray-500">{chapters} Chapters • {formatTotalDuration(module.content?.reduce((sum, item) => sum + (parseFloat(item.duration) || 0), 0) || 0)}</p>
                  </div>
                  <Icon
                    icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                    className="shrink-0 text-gray-400 w-5 h-5"
                  />
                </button>

                {isExpanded && (
                  <div
                    id={`module-content-${module.id}`}
                    className="mt-1 space-y-1 pl-2"
                  >
                    {Array.isArray(module.content) &&
                      module.content.map((item) => {
                        const isActive = selectedContent?.id === item.id && selectedContent?.type === item.type;
                        const isCompleted = item.completed === 1;
                        const isLocked = item.isLocked || module.isLocked;
                        return (
                          <button
                            ref={isActive ? activeItemRef : undefined}
                            key={`${item.type || 'content'}-${item.id}-${item.serial_no ?? 0}`}
                            type="button"
                            onClick={() => !isLocked && onContentClick(item)}
                            disabled={isLocked}
                            className={`w-full flex items-center gap-3 py-2.5 px-2 rounded-lg text-left transition-all ${
                              isLocked ? 'opacity-50 cursor-not-allowed' : isActive ? 'bg-[#7083FF]/5' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span
                              className={`shrink-0 flex items-center justify-center w-6 h-6 rounded-full border-2 transition-all ${
                                isLocked
                                  ? 'border-gray-300 bg-gray-100'
                                  : isCompleted 
                                  ? 'bg-[#7083FF] border-[#7083FF]' 
                                  : 'border-gray-300 bg-transparent'
                              }`}
                            >
                              {isLocked ? (
                                <Icon icon="mdi:lock" width="12" height="12" className="text-gray-400" />
                              ) : isCompleted ? (
                                <Icon icon="mdi:check" width="12" height="12" className="text-white" />
                              ) : null}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className={`block text-sm font-medium truncate ${isLocked ? 'text-gray-400' : 'text-gray-700'}`}>{item.title}</span>
                              <span className="block text-xs text-gray-500 mt-0.5">{formatDuration(item.duration)}</span>
                            </span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default CourseModule;